import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/store/user';

// 路由元信息类型定义
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean; // 是否需要登录
    title?: string; // 页面标题
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: {
      requiresAuth: false,
      title: '登录'
    }
  },
  {
    path: '/',
    name: 'Home',
    component: () => import('@/features/yellow-pages/views/YellowHome.vue'),
    meta: {
      requiresAuth: false,
      title: '首页'
    }
  },
  // 404 页面
  {
    path: '/home',
    name: 'YellowHome',
    redirect: '/',
    meta: {
      requiresAuth: false,
      title: '留学中国黄页'
    }
  },
  {
    path: '/universities',
    name: 'Universities',
    component: () => import('@/features/yellow-pages/views/UniversitiesPage.vue'),
    meta: {
      requiresAuth: false,
      title: '院校'
    }
  },
  {
    path: '/cities',
    name: 'Cities',
    component: () => import('@/features/yellow-pages/views/CitiesPage.vue'),
    meta: {
      requiresAuth: false,
      title: '城市'
    }
  },
  {
    path: '/cities/compare',
    name: 'CityComparison',
    component: () => import('@/features/yellow-pages/views/CityComparisonPage.vue'),
    meta: {
      requiresAuth: false,
      title: '城市对比'
    }
  },
  {
    path: '/cities/:citySlug',
    name: 'CityDetail',
    component: () => import('@/features/yellow-pages/views/CityDetailPage.vue'),
    meta: {
      requiresAuth: false,
      title: '城市详情'
    }
  },
  {
    path: '/majors',
    name: 'Majors',
    component: () => import('@/features/yellow-pages/views/MajorsPage.vue'),
    meta: {
      requiresAuth: false,
      title: '专业'
    }
  },
  {
    path: '/scholarships',
    name: 'Scholarships',
    component: () => import('@/features/yellow-pages/views/ScholarshipsPage.vue'),
    meta: {
      requiresAuth: false,
      title: '奖学金'
    }
  },
  {
    path: '/cases',
    name: 'ApplicationCases',
    component: () => import('@/features/yellow-pages/views/ApplicationCasesPage.vue'),
    meta: {
      requiresAuth: false,
      title: '申请案例'
    }
  },
  {
    path: '/cases/:caseId',
    name: 'ApplicationCaseDetail',
    component: () => import('@/features/yellow-pages/views/ApplicationCaseDetailPage.vue'),
    meta: {
      requiresAuth: false,
      title: '申请案例详情'
    }
  },
  {
    path: '/news',
    name: 'NewsList',
    component: () => import('@/features/yellow-pages/views/NewsListPage.vue'),
    meta: {
      requiresAuth: false,
      title: '资讯'
    }
  },
  {
    path: '/account',
    name: 'AccountCenter',
    component: () => import('@/features/yellow-pages/views/AccountCenter.vue'),
    meta: {
      requiresAuth: false,
      title: '账号中心'
    }
  },
  {
    path: '/account/security',
    name: 'AccountSecurity',
    component: () => import('@/features/yellow-pages/views/AccountSecurity.vue'),
    meta: {
      requiresAuth: false,
      title: '登录与安全'
    }
  },
  {
    path: '/account/connections',
    name: 'AccountConnections',
    component: () => import('@/features/yellow-pages/views/AccountConnections.vue'),
    meta: {
      requiresAuth: false,
      title: '关联账号'
    }
  },
  {
    path: '/account/favorites',
    name: 'AccountFavorites',
    component: () => import('@/features/yellow-pages/views/AccountFavorites.vue'),
    meta: {
      requiresAuth: false,
      title: '我的收藏'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/404.vue'),
    meta: {
      requiresAuth: false,
      title: '页面不存在'
    }
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes
});

// 全局路由守卫
router.beforeEach((to, _from, next) => {
  const userStore = useUserStore();
  const { requiresAuth = true } = to.meta;

  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - ${import.meta.env.VITE_APP_TITLE || 'Vite Project'}`;
  }

  // 需要登录但未登录，跳转到登录页
  if (requiresAuth && !userStore.isLogin) {
    next({
      path: '/login',
      query: { redirect: to.fullPath } // 保存跳转前的路径
    });
  } else if (to.path === '/login' && userStore.isLogin) {
    // 已登录访问登录页，跳转到首页
    next('/');
  } else {
    next();
  }
});

export default router;
